import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';
import { Box, Button, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { t } from '../i18n/sq';

const RELOAD_KEY = 'pd.reloadedAfterUpdate';

/** After a new version is deployed, screens still open ask for files that no longer exist. */
const isStaleVersion = (e: unknown) =>
  e instanceof Error &&
  /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(e.message);

/**
 * Shown instead of the framework's developer screen whenever a page crashes. Plain Albanian,
 * one obvious action; technical details only in development.
 */
export function RouteError() {
  const error = useRouteError();

  // A new version was deployed while the page was open: reload once to get it.
  useEffect(() => {
    try {
      if (isStaleVersion(error) && !sessionStorage.getItem(RELOAD_KEY)) {
        sessionStorage.setItem(RELOAD_KEY, '1');
        window.location.reload();
      }
    } catch {
      /* storage unavailable: show the page below */
    }
  }, [error]);

  const notFound = isRouteErrorResponse(error) && error.status === 404;

  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', p: 3 }}>
      <Stack spacing={2} sx={{ maxWidth: 480, textAlign: 'center', alignItems: 'center' }}>
        <Typography variant="h5" component="h1">
          {notFound ? t.notFound.title : t.errorPage.title}
        </Typography>
        <Typography color="text.secondary">{notFound ? '' : t.errorPage.body}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            variant="contained"
            size="large"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            {t.errorPage.reload}
          </Button>
          <Button size="large" href="/">
            {t.notFound.back}
          </Button>
        </Stack>
        {import.meta.env.DEV && error instanceof Error && (
          <Box
            component="pre"
            sx={{
              textAlign: 'left',
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              color: 'error.main',
              maxWidth: '90vw',
            }}
          >
            {error.message}
          </Box>
        )}
      </Stack>
    </Box>
  );
}
